import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

import { Container, Loading, ErrorMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';
import type { SubmissionReportFilterState } from './models/submissionReportFilterDTOs';
import type { SubmissionReportRowDTO } from './models/submissionReportDTOs';
import { useAuth } from '@/src/app/context/useAuth';
import SubmissionReportFilters from './components/SubmissionReportFilters';
import SubmissionReportLegend from './components/SubmissionReportLegend';
import SubmissionReportTable from './components/SubmissionReportTable';

const SubmissionReportPage: React.FC = () => {
    document.title = 'Submission Report';

    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState<SubmissionReportRowDTO[]>([]);
    const [hasSearched, setHasSearched] = useState(false);

    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const UNIVERSAL_FILTER_KEY = `universal-filters-${userId}`;

    const [examName, setExamName] = useState(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) return JSON.parse(saved)?.examName || '';
        } catch {
            // intentionally empty
        }
        return '';
    });
    const pendingExamNameRef = useRef('');

    const [filterState, setFilterState] = useState<SubmissionReportFilterState>(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    districtId: parsed.districtId || 0,
                    schoolId: parsed.schoolId || 0,
                    gradeLevelMasterId: parsed.gradeLevelMasterId || 0,
                    classId: parsed.classId || 0,
                    examTypeMasterId: parsed.examTypeMasterId || 0,
                    examId:
                        parsed.examId ||
                        (parsed.examIds && parsed.examIds.length > 0 ? parsed.examIds[0] : 0),
                };
            }
        } catch {
            // intentionally empty
        }
        return {
            districtId: 0,
            schoolId: 0,
            gradeLevelMasterId: 0,
            classId: 0,
            examTypeMasterId: 0,
            examId: 0,
        };
    });

    useEffect(() => {
        if (!userId) return;
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            const existingFilters = saved ? JSON.parse(saved) : {};

            const universalFilters = {
                ...existingFilters,
                gradeLevelMasterId: filterState.gradeLevelMasterId,
                districtId: filterState.districtId,
                schoolId: filterState.schoolId,
                classId: filterState.classId,
                examTypeMasterId: filterState.examTypeMasterId,
                examId: filterState.examId,
                examIds: [filterState.examId],
                examName,
            };
            localStorage.setItem(UNIVERSAL_FILTER_KEY, JSON.stringify(universalFilters));
        } catch {
            // intentionally empty
        }
    }, [filterState, examName, userId, UNIVERSAL_FILTER_KEY]);

    const refreshTableData = useCallback(
        async ({
            districtId,
            schoolId,
            gradeLevelMasterId,
            classId,
            examTypeMasterId,
            examId,
        }: SubmissionReportFilterState = filterState) => {
            setError('');
            setLoaded(false);
            setHasSearched(true);

            if (!districtId || !schoolId || !gradeLevelMasterId) {
                setError('Please select District, School, and Grade.');
                setLoaded(true);
                return;
            }

            let requestUrl = `reports/submissionReport/GetSubmissionReport?districtId=${districtId}`;
            requestUrl += `&schoolId=${schoolId}`;
            requestUrl += `&gradeId=${gradeLevelMasterId}`;
            requestUrl += `&classId=${classId}`;
            requestUrl += `&examTypeId=${examTypeMasterId}`;
            requestUrl += `&examId=${examId}`;

            try {
                const res = await apiRequest<SubmissionReportRowDTO[]>('GET', requestUrl);
                setData(Array.isArray(res.data) ? res.data : []);
                if (pendingExamNameRef.current) setExamName(pendingExamNameRef.current);
            } catch {
                setError('Failed to load Submission Report');
            } finally {
                setLoaded(true);
            }
        },
        [filterState]
    );

    useEffect(() => {
        const fs = filterState;
        if (fs.districtId && fs.schoolId && fs.gradeLevelMasterId) {
            refreshTableData(fs);
        } else {
            setLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Container>
            <h1 className="page-header">Submission Report</h1>
            <br />

            <SubmissionReportFilters
                filterState={filterState}
                setFilterState={setFilterState}
                refreshTableData={refreshTableData}
                onExamNameChange={(name) => {
                    pendingExamNameRef.current = name;
                }}
            />

            {examName && (
                <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '8px' }}>
                    {examName}
                </h2>
            )}

            <Box
                sx={{
                    pb: 2,
                }}
            >
                <SubmissionReportLegend dataLoaded={data !== null && data.length !== 0} />
            </Box>

            <Loading visible={hasSearched && !loaded} message="Loading Submission Report..." />

            <ErrorMessage error={hasSearched ? error : ''} />

            {hasSearched && loaded && !error && <SubmissionReportTable rows={data} />}
        </Container>
    );
};

export default SubmissionReportPage;
